import React, { useState, useRef, useEffect } from 'react';
import {
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography,
  Alert,
  CircularProgress,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import heLocale from 'date-fns/locale/he';
import { format } from 'date-fns';
import API from '../../services/api';
import PaymentMethodRows from '../components/PaymentMethodRows';
import MobileDialog from './MobileDialog';

const CURRENCY_SYMBOLS = { ILS: '₪', USD: '$', EUR: '€' };

const SupplierPaymentDialog = ({ open, onClose, supplier, onSuccess }) => {
  const [amount, setAmount] = useState('');
  const [actualDate, setActualDate] = useState(new Date());
  const [invoiceNumber, setInvoiceNumber] = useState('');
  const [shippingDocument, setShippingDocument] = useState('');
  const [notes, setNotes] = useState('');
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [formStep, setFormStep] = useState('form');
  const [error, setError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const isSubmittingRef = useRef(false);
  const errorRef = useRef(null);
  const [exchangeRate, setExchangeRate] = useState('');

  const isNonILS = supplier?.Currency && supplier.Currency !== 'ILS';
  const currencySymbol = CURRENCY_SYMBOLS[supplier?.Currency] || '₪';

  // Scroll to error when it appears
  useEffect(() => {
    if (error && errorRef.current) {
      errorRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, [error]);

  // Fetch exchange rate when dialog opens for non-ILS supplier
  useEffect(() => {
    if (open && isNonILS) {
      API.get(`/supplier/exchange-rate/?currency=${supplier.Currency}`)
        .then((res) => setExchangeRate(String(res.data.rate)))
        .catch(() => {
          API.get(`/supplier/transactions/?supplier_id=${supplier.SupplierID}`)
            .then((res) => {
              const lastForeign = res.data?.find(t => t.ExchangeRate && parseFloat(t.ExchangeRate) !== 1);
              if (lastForeign) setExchangeRate(String(lastForeign.ExchangeRate));
            })
            .catch(() => {});
        });
    }
  }, [open, supplier?.SupplierID, supplier?.Currency, isNonILS]);

  const resetForm = () => {
    setAmount('');
    setActualDate(new Date());
    setInvoiceNumber('');
    setShippingDocument('');
    setNotes('');
    setPaymentMethods([]);
    setFormStep('form');
    setError('');
    setExchangeRate('');
    isSubmittingRef.current = false;
    setIsSubmitting(false);
  };

  const handleClose = () => {
    if (isSubmitting) return;
    resetForm();
    onClose();
  };

  const handleValidation = () => {
    setError('');
    const parsedAmount = parseFloat(amount);
    if (Number.isNaN(parsedAmount) || parsedAmount <= 0) {
      setError('סכום התשלום חייב להיות גדול מאפס');
      return;
    }

    // Validate payment methods if provided
    if (paymentMethods.length > 0) {
      const pmTotal = paymentMethods.reduce((sum, pm) => sum + (parseFloat(pm.amount) || 0), 0);
      if (Math.abs(pmTotal - parsedAmount) > 0.01) {
        setError('סכום אמצעי התשלום אינו תואם לסכום הכולל');
        return;
      }

      // Check that all check payment methods have check number
      const invalidChecks = paymentMethods.filter(
        pm => pm.type === 'check' && !pm.check_number
      );
      if (invalidChecks.length > 0) {
        setError("יש להזין מספר צ'ק לכל תשלום בצ'ק");
        return;
      }
    }

    setFormStep('confirm');
  };

  const handleSubmit = async () => {
    if (isSubmittingRef.current) return;
    isSubmittingRef.current = true;
    setIsSubmitting(true);
    setError('');

    try {
      const payload = {
        supplier_id: supplier.SupplierID,
        amount: parseFloat(amount),
        actual_date: format(actualDate, 'yyyy-MM-dd'),
        invoice_number: invoiceNumber,
        shipping_document: shippingDocument,
        note: notes,
      };

      // Add exchange rate for non-ILS suppliers
      if (isNonILS && exchangeRate) {
        payload.exchange_rate = parseFloat(exchangeRate);
      }

      // Add payment methods if provided
      if (paymentMethods.length > 0) {
        payload.payment_methods = paymentMethods.map(pm => ({
          type: pm.type,
          amount: parseFloat(pm.amount) || 0,
          // Check fields
          check_number: pm.check_number || '',
          check_bank_name: pm.check_bank_name || '',
          check_account_number: pm.check_account_number || '',
          check_due_date: pm.check_due_date ? format(pm.check_due_date, 'yyyy-MM-dd') : null,
          // Card fields
          card_last_four: pm.card_last_four || '',
          card_type: pm.card_type || '',
          // Transfer fields
          bank_name: pm.bank_name || '',
          bank_account_last4: pm.bank_account_last4 || '',
          transfer_date: pm.transfer_date ? format(pm.transfer_date, 'yyyy-MM-dd') : null,
          notes: pm.notes || '',
        }));
      }

      await API.post('/supplier/payment/', payload);
      if (onSuccess) onSuccess();
      resetForm();
      onClose();
    } catch (err) {
      console.error('Error recording payment:', err);
      setError(err?.response?.data?.error || 'שגיאה ברישום תשלום');
      setIsSubmitting(false);
      isSubmittingRef.current = false;
    }
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={heLocale}>
      <MobileDialog
        open={open}
        onClose={(event, reason) => {
          if (reason === 'backdropClick') return;
          handleClose();
        }}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            fontSize: { xs: '1rem', sm: '1.4rem' },
            '*': { fontSize: 'inherit !important' },
          },
        }}
      >
        <DialogTitle sx={{ fontSize: { xs: '1.2rem !important', sm: '2rem !important' }, fontWeight: 'bold' }}>
          רישום תשלום לספק
        </DialogTitle>
        <DialogContent dividers>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {error && <Alert ref={errorRef} severity="error">{error}</Alert>}

            {formStep === 'form' && (
              <>
                <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                  ספק: {supplier?.SupplierName}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  יתרה נוכחית:{' '}
                  {isNonILS
                    ? <>
                        {new Intl.NumberFormat('he-IL', { style: 'currency', currency: supplier.Currency }).format(supplier?.ForeignBalance || 0)}
                        {' '}({new Intl.NumberFormat('he-IL', { style: 'currency', currency: 'ILS' }).format(supplier?.Balance || 0)})
                      </>
                    : new Intl.NumberFormat('he-IL', { style: 'currency', currency: 'ILS' }).format(supplier?.Balance || 0)
                  }
                </Typography>

                <TextField
                  label={`סכום תשלום${isNonILS ? ` (${currencySymbol})` : ''}`}
                  type="number"
                  fullWidth
                  value={amount}
                  onChange={(e) => setAmount(e.target.value)}
                  onWheel={(e) => e.target.blur()}
                  onFocus={(e) => e.target.select()}
                  disabled={isSubmitting}
                  sx={{
                    direction: 'ltr',
                    '& input': {
                      textAlign: 'center',
                    },
                  }}
                />

                {isNonILS && (
                  <>
                    <TextField
                      label={`שער חליפין (1 ${supplier.Currency} = ? ₪)`}
                      type="number"
                      fullWidth
                      value={exchangeRate}
                      onChange={(e) => setExchangeRate(e.target.value)}
                      onWheel={(e) => e.target.blur()}
                      onFocus={(e) => e.target.select()}
                      disabled={isSubmitting}
                      helperText="שער מעודכן מבנק ישראל - ניתן לשנות"
                      sx={{ direction: 'ltr', '& input': { textAlign: 'center' } }}
                    />
                    {amount && exchangeRate && (
                      <Typography variant="body2" color="text.secondary">
                        ≈ {new Intl.NumberFormat('he-IL', { style: 'currency', currency: 'ILS' }).format(
                          parseFloat(amount) * parseFloat(exchangeRate)
                        )} (שווי ב-₪)
                      </Typography>
                    )}
                  </>
                )}

                <DatePicker
                  label="תאריך בפועל"
                  value={actualDate}
                  onChange={(newValue) => setActualDate(newValue)}
                  disabled={isSubmitting}
                  slotProps={{
                    textField: {
                      fullWidth: true,
                    },
                  }}
                />

                <TextField
                  label="מספר חשבונית"
                  value={invoiceNumber}
                  onChange={(e) => setInvoiceNumber(e.target.value)}
                  fullWidth
                  disabled={isSubmitting}
                />

                <TextField
                  label="תעודת משלוח"
                  value={shippingDocument}
                  onChange={(e) => setShippingDocument(e.target.value)}
                  fullWidth
                  disabled={isSubmitting}
                />

                <TextField
                  label="הערות"
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  multiline
                  minRows={2}
                  fullWidth
                  disabled={isSubmitting}
                />

                {/* Payment Methods Section */}
                <PaymentMethodRows
                  paymentMethods={paymentMethods}
                  setPaymentMethods={setPaymentMethods}
                  totalAmount={parseFloat(amount) || 0}
                  disabled={isSubmitting}
                  compact={false}
                  currencySymbol={currencySymbol}
                />
              </>
            )}

            {formStep === 'confirm' && (
              <>
                {isSubmitting ? (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, justifyContent: 'center', py: 2 }}>
                    <CircularProgress size={24} />
                    <Typography>מעדכן...</Typography>
                  </Box>
                ) : (
                  <>
                    <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 1 }}>
                      אישור תשלום
                    </Typography>
                    <Typography>ספק: {supplier?.SupplierName}</Typography>
                    <Typography>סכום: {currencySymbol}{amount}{isNonILS && exchangeRate && ` (≈ ₪${(parseFloat(amount) * parseFloat(exchangeRate)).toFixed(2)})`}</Typography>
                    {invoiceNumber && <Typography>חשבונית: {invoiceNumber}</Typography>}
                    {shippingDocument && <Typography>תעודת משלוח: {shippingDocument}</Typography>}
                    {notes && <Typography>הערות: {notes}</Typography>}

                    {/* Show payment methods in confirmation */}
                    {paymentMethods.length > 0 && (
                      <>
                        <Typography sx={{ mt: 2, fontWeight: 'bold' }}>
                          אמצעי תשלום:
                        </Typography>
                        {paymentMethods.map((pm, index) => {
                          const typeLabels = {
                            cash: 'מזומן',
                            check: "צ'ק",
                            credit_card: 'כרטיס אשראי',
                            bank_transfer: 'העברה בנקאית',
                            other: 'אחר'
                          };
                          let details = '';
                          if (pm.type === 'check' && pm.check_number) {
                            details = ` - צ'ק #${pm.check_number}`;
                            if (pm.check_bank_name) details += ` (${pm.check_bank_name})`;
                            if (pm.check_due_date) details += ` לפירעון: ${format(pm.check_due_date, 'dd/MM/yyyy')}`;
                          } else if (pm.type === 'credit_card' && pm.card_last_four) {
                            details = ` - ****${pm.card_last_four}`;
                          } else if (pm.type === 'bank_transfer' && pm.bank_name) {
                            details = ` - ${pm.bank_name}`;
                          }
                          return (
                            <Typography key={index}>
                              {typeLabels[pm.type] || pm.type}: {parseFloat(pm.amount).toFixed(2)} {isNonILS ? currencySymbol : '₪'}{details}
                            </Typography>
                          );
                        })}
                      </>
                    )}
                  </>
                )}
              </>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} size="large" disabled={isSubmitting}>
            ביטול
          </Button>
          {formStep === 'form' && (
            <Button
              variant="contained"
              onClick={handleValidation}
              size="large"
              disabled={isSubmitting}
            >
              שלב הבא
            </Button>
          )}
          {formStep === 'confirm' && (
            <>
              <Button
                onClick={() => setFormStep('form')}
                size="large"
                disabled={isSubmitting}
              >
                חזרה
              </Button>
              <Button
                variant="contained"
                onClick={handleSubmit}
                disabled={isSubmitting}
                size="large"
              >
                {isSubmitting ? <CircularProgress size={24} /> : 'אישור'}
              </Button>
            </>
          )}
        </DialogActions>
      </MobileDialog>
    </LocalizationProvider>
  );
};

export default SupplierPaymentDialog;
